import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LogOut, Users, CheckCircle, AlertCircle, Clock, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import LeadTrendChart from '../components/LeadTrendChart';

const BACKEND_URL = '';

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, synced: 0, pending: 0, failed: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const token = sessionStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/admin'); return; }
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      const [leadsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/leads?page=${page}&limit=15`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/leads/stats`, { headers }),
      ]);
      if (leadsRes.status === 401 || statsRes.status === 401) {
        sessionStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      setLeads(leadsData.leads);
      setTotalPages(leadsData.pages);
      setStats(statsData);
    } catch {
      sessionStorage.removeItem('admin_token');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [token, page, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const logout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const statusBadge = (status) => {
    const map = {
      synced: 'bg-emerald-100 text-emerald-800',
      pending: 'bg-amber-100 text-amber-800',
      crm_failed: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-slate-100 text-slate-600'}`} data-testid={`lead-status-${status}`}>
        {status || 'unknown'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-emerald-950 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/solvida-logo-new.png" alt="SolVida" className="h-10 w-auto" />
            <h1 className="text-lg font-bold hidden sm:block" data-testid="admin-dashboard-title">Lead Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="ghost" onClick={fetchData} className="text-white hover:bg-white/10" data-testid="refresh-button">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" onClick={logout} className="text-white hover:bg-white/10" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-slate-600" data-testid="stat-total">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Total Leads</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <Users className="w-10 h-10 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500" data-testid="stat-synced">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Synced to CRM</p>
                  <p className="text-3xl font-bold text-emerald-700">{stats.synced}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500" data-testid="stat-pending">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">Pending</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.pending}</p>
                </div>
                <Clock className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500" data-testid="stat-failed">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">CRM Failed</p>
                  <p className="text-3xl font-bold text-red-700">{stats.failed}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lead Trend Chart */}
        <LeadTrendChart token={token} />

        {/* Leads Table */}
        <Card className="shadow-lg" data-testid="leads-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-600">Name</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Email</th>
                    <th className="text-left p-4 font-semibold text-slate-600 hidden md:table-cell">Phone</th>
                    <th className="text-left p-4 font-semibold text-slate-600 hidden lg:table-cell">State</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Status</th>
                    <th className="text-left p-4 font-semibold text-slate-600 hidden md:table-cell">Date</th>
                    <th className="text-left p-4 font-semibold text-slate-600 hidden xl:table-cell">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading...</td></tr>
                  ) : leads.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-400" data-testid="no-leads-message">No leads found</td></tr>
                  ) : (
                    leads.map((lead, i) => (
                      <tr key={lead.request_id || i} className="border-b hover:bg-slate-50 transition-colors" data-testid={`lead-row-${i}`}>
                        <td className="p-4 font-medium text-slate-900">{lead.name}</td>
                        <td className="p-4 text-slate-600">{lead.email}</td>
                        <td className="p-4 text-slate-600 hidden md:table-cell">{lead.phone}</td>
                        <td className="p-4 text-slate-600 hidden lg:table-cell">{lead.state || '—'}</td>
                        <td className="p-4">{statusBadge(lead.status)}</td>
                        <td className="p-4 text-slate-500 hidden md:table-cell whitespace-nowrap">
                          {lead.submitted_at ? new Date(lead.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="p-4 text-slate-500 hidden xl:table-cell max-w-xs truncate">{lead.message}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t" data-testid="pagination">
                <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
